import { Fragment, useRef, useState } from "react";

import clsx from "clsx";
import moment from "moment";
import { Controller, type UseFormReturn } from "react-hook-form";
import { useOnClickOutside } from "usehooks-ts";
import { type z } from "zod";

import { formatDate } from "@features/mmm/utils/formatters";
import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
} from "@headlessui/react";
import { DateRangeCalendar } from "@shared/date-range-calendar";
import { renderChevronAnimation } from "@shared/selects/utils";

import { useShowReferencePeriod } from "../../hooks/use-show-reference-period";

import { type ClientSettingSchema } from "./client-setting-form";

type Props = {
  formMeta: UseFormReturn<z.infer<typeof ClientSettingSchema>>;
  isDateRangeUnder2Weeks: boolean;
};

export const SettingReferencePeriodField = ({
  formMeta,
  isDateRangeUnder2Weeks,
}: Props) => {
  const enableMmmReferencePeriod = useShowReferencePeriod();

  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  if (!enableMmmReferencePeriod) {
    return null;
  }

  return (
    <div className="border-b border-v2-secondary px-6 pt-5 pb-8">
      <label className="block font-semibold">
        4. Select a reference period
      </label>
      <p className="text-sm mb-5 max-w-[42rem]">
        Select the historical period to use as the starting point for your
        optimized spend. Optimized mixes will anchor to what your spend was
        during this period, while making adjustments to improve efficiency and
        performance outcomes at your chosen target spend.
      </p>
      <Controller
        control={formMeta.control}
        name="reference_start_date"
        render={({
          field: { value: startDate, onChange: onStartDateChange },
        }) => (
          <Controller
            control={formMeta.control}
            name="reference_end_date"
            render={({
              field: { value: endDate, onChange: onEndDateChange },
            }) => (
              <div>
                <div className="flex items-center gap-1">
                  <div
                    className="flex items-center cursor-pointer"
                    onClick={() => setIsCalendarOpen(!isCalendarOpen)}
                  >
                    <i className="fa-regular fa-calendar text-cosmic mr-3" />
                    <span className="font-semibold whitespace-nowrap tracking-wider underline text-cosmic">
                      {`${formatDate(startDate ?? "")} - ${formatDate(
                        endDate ?? "",
                      )}`}
                    </span>
                  </div>
                  <HistoricalMixDatePicker
                    startDate={startDate ?? "2024-01-01"}
                    endDate={endDate ?? "2024-01-31"}
                    onChooseDateRange={({ start_date, end_date }) => {
                      onStartDateChange(start_date);
                      onEndDateChange(end_date);
                    }}
                    isCalendarOpen={isCalendarOpen}
                    setIsCalendarOpen={setIsCalendarOpen}
                  />
                </div>
                {isDateRangeUnder2Weeks && (
                  <div className="w-full text-xs text-mars+1 mt-2">
                    Reference period must be at least 14 days long.
                  </div>
                )}
              </div>
            )}
          />
        )}
      />
    </div>
  );
};

function HistoricalMixDatePicker({
  startDate,
  endDate,
  onChooseDateRange,
  isCalendarOpen,
  setIsCalendarOpen,
}: {
  startDate: string;
  endDate: string;
  onChooseDateRange: ({
    start_date,
    end_date,
  }: {
    start_date: string;
    end_date: string;
  }) => void;
  isCalendarOpen: boolean;
  setIsCalendarOpen: (isCalendarOpen: boolean) => void;
}) {
  const ref = useRef(null);

  useOnClickOutside(ref, () => setIsCalendarOpen(false));

  return (
    <Popover className="nb-input-container relative">
      <PopoverButton
        className="hover:bg-cosmic-4 rounded-md w-6 h-6"
        onClick={() => setIsCalendarOpen(!isCalendarOpen)}
      >
        {renderChevronAnimation(isCalendarOpen, false, null, "text-cosmic")}
      </PopoverButton>
      {isCalendarOpen && (
        <Transition
          show={isCalendarOpen}
          as={Fragment}
          enter="transition ease-out duration-200"
          enterFrom="opacity-0 translate-y-1"
          enterTo="opacity-100 translate-y-0"
          leave="transition ease-in duration-150"
          leaveFrom="opacity-100 translate-y-0"
          leaveTo="opacity-0 translate-y-1"
        >
          <PopoverPanel
            static
            ref={ref}
            anchor="top"
            className={clsx(
              "z-[9998] mt-0 elevation-1",
              "rounded-md py-1 bg-v2-primary flex flex-col space-y-2 w-fit",
            )}
          >
            <DateRangeCalendar
              // 2 years
              maxLookbackDays={2 * 365}
              maxAllowedDate={moment().subtract(1, "day")}
              value={{ startDate, endDate }}
              onApply={({ startDate: newStartDate, endDate: newEndDate }) => {
                onChooseDateRange({
                  start_date: newStartDate,
                  end_date: newEndDate,
                });
                setIsCalendarOpen(false);
              }}
            />
          </PopoverPanel>
        </Transition>
      )}
    </Popover>
  );
}
